import React, { useMemo, useState } from 'react'

export interface TimelinePoint {
  at: string
  collationCount: number
  daysRunning?: number
  isActive?: boolean
}

interface FireSparklineProps {
  timeline: TimelinePoint[]
  /** Alto del trazado en px. 40 para tarjeta, 140 para detalle. */
  height?: number
  /** Muestra ejes y rejilla discreta. Solo en la vista de detalle. */
  detailed?: boolean
}

/** Serie única: un solo color, el del acento de la app. Sin leyenda — el título la nombra. */
const LINE = '#e879f9'
const INK_MUTED = '#a8a29e'
const GRID = '#44403c'

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })

/**
 * Evolución de copias activas del creativo en el tiempo.
 *
 * Es el dato que distingue una captura de un guardado: un 11 no dice nada, pero
 * "de 3 a 11 en cinco días" dice que está escalando ahora.
 */
export default function FireSparkline({ timeline, height = 40, detailed = false }: FireSparklineProps) {
  const [hover, setHover] = useState<number | null>(null)

  const width = detailed ? 640 : 200
  const padX = detailed ? 34 : 4
  const padY = detailed ? 14 : 6

  const points = useMemo(() => {
    const clean = (timeline || []).filter(p => p && typeof p.collationCount === 'number')
    if (clean.length === 0) return []

    const max = Math.max(...clean.map(p => p.collationCount), 1)
    const min = Math.min(...clean.map(p => p.collationCount), 0)
    const span = Math.max(max - min, 1)
    const stepX = clean.length > 1 ? (width - padX * 2) / (clean.length - 1) : 0

    return clean.map((p, i) => ({
      ...p,
      x: padX + i * stepX,
      y: padY + (height - padY * 2) * (1 - (p.collationCount - min) / span),
      max,
      min
    }))
  }, [timeline, width, height, padX, padY])

  if (points.length === 0) {
    return (
      <div className="flex items-center text-xs text-stone-500" style={{ height }}>
        Sin histórico todavía
      </div>
    )
  }

  // Un solo avistamiento: no hay línea que trazar, solo el punto y el valor.
  if (points.length === 1) {
    const only = points[0]
    return (
      <div className="flex items-center gap-2 text-xs text-stone-400" style={{ height }}>
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: LINE }} />
        {only.collationCount} {only.collationCount === 1 ? 'copia' : 'copias'} · primer avistamiento
      </div>
    )
  }

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')
  const last = points[points.length - 1]
  const active = hover !== null ? points[hover] : null

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Copias activas en el tiempo: de ${points[0].collationCount} a ${last.collationCount}`}
        onMouseLeave={() => setHover(null)}
      >
        {detailed && (
          <>
            {/* Rejilla recesiva: solo mínimo y máximo. */}
            {[points[0].min, points[0].max].map((v, i) => {
              const y = padY + (height - padY * 2) * (i === 0 ? 1 : 0)
              return (
                <g key={v}>
                  <line x1={padX} y1={y} x2={width - padX} y2={y} stroke={GRID} strokeWidth={1} />
                  <text x={4} y={y + 3} fontSize={9} fill={INK_MUTED}>
                    {v}
                  </text>
                </g>
              )
            })}
          </>
        )}

        <path d={path} fill="none" stroke={LINE} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        {/* Punto final siempre visible: es el valor actual. */}
        <circle cx={last.x} cy={last.y} r={3.5} fill={LINE} stroke="#1c1917" strokeWidth={2} />

        {active && (
          <>
            <line x1={active.x} y1={padY} x2={active.x} y2={height - padY} stroke={GRID} strokeWidth={1} />
            <circle cx={active.x} cy={active.y} r={4.5} fill={LINE} stroke="#1c1917" strokeWidth={2} />
          </>
        )}

        {/* Zonas de contacto más anchas que la marca. */}
        {points.map((p, i) => (
          <rect
            key={i}
            x={p.x - Math.max(6, (width - padX * 2) / points.length / 2)}
            y={0}
            width={Math.max(12, (width - padX * 2) / points.length)}
            height={height}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>

      {active && (
        <div
          className="absolute z-20 -translate-x-1/2 -translate-y-full pointer-events-none whitespace-nowrap rounded-lg border border-stone-700 bg-stone-900 px-2 py-1 text-xs text-stone-100 shadow-lg"
          style={{ left: `${(active.x / width) * 100}%`, top: -4 }}
        >
          <span className="font-semibold">{active.collationCount}</span>{' '}
          {active.collationCount === 1 ? 'copia' : 'copias'}
          <span className="text-stone-400"> · {fmtDate(active.at)}</span>
        </div>
      )}
    </div>
  )
}
